import React from 'react';
import { Building2, MapPin, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SettingsEmptyState from '@/components/settings/SettingsEmptyState';

function actionButtonProps(testId, label) {
  return {
    type: 'button',
    size: 'sm',
    variant: 'outline',
    className: 'shrink-0',
    'data-testid': testId,
    'aria-label': label,
  };
}

export function SettingsCompanyList({
  companies = [],
  canCreate,
  canUpdate,
  onCreate,
  onEdit,
  testId = 'settings-companies',
}) {
  if (!companies.length) {
    return <SettingsEmptyState title="Nessuna azienda disponibile" description="Non risultano aziende nel perimetro autorizzato." testId={`${testId}-empty`} />;
  }

  return (
    <div className="space-y-3" data-testid={testId}>
      {canCreate && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onCreate}
            className="bg-brand-green text-white hover:bg-brand-green-hover"
            data-testid={`${testId}-add`}
          >
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Aggiungi azienda
          </Button>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {companies.map((company) => (
          <article
            key={company.id}
            className="flex min-w-0 items-start gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5"
            data-testid={`settings-company-card-${company.id}`}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-brand-green">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display text-lg font-semibold text-brand-navy">{company.name}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {company.slug ? `Codice azienda: ${company.slug}` : 'Codice azienda non impostato'}
              </p>
            </div>
            {canUpdate && (
              <Button
                {...actionButtonProps(`settings-edit-company-${company.id}`, `Modifica ${company.name}`)}
                onClick={() => onEdit(company)}
              >
                <Pencil className="mr-1 h-4 w-4" aria-hidden="true" /> Modifica
              </Button>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

export function SettingsLocationList({
  companies = [],
  locationsByCompany = {},
  canCreate,
  canUpdate,
  onCreate,
  onEdit,
  testId = 'settings-locations',
}) {
  if (!companies.length) {
    return <SettingsEmptyState title="Nessun locale disponibile" description="Non risultano locali nel perimetro autorizzato." testId={`${testId}-empty`} />;
  }

  return (
    <div className="space-y-5" data-testid={testId}>
      {companies.map((company) => {
        const locations = locationsByCompany[company.id] || [];
        return (
          <section key={company.id} aria-labelledby={`${testId}-company-${company.id}`}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 id={`${testId}-company-${company.id}`} className="font-display text-base font-semibold text-brand-navy">
                {company.name}
              </h2>
              {canCreate && (
                <Button
                  {...actionButtonProps(`${testId}-add-${company.id}`, `Aggiungi un locale a ${company.name}`)}
                  onClick={() => onCreate(company.id)}
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Aggiungi locale
                </Button>
              )}
            </div>
            {locations.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {locations.map((location) => (
                  <article
                    key={location.id}
                    className="flex min-w-0 items-start gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5"
                    data-testid={`settings-location-card-${location.id}`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-brand-blue">
                      <MapPin className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-stone-800">{location.name}</h3>
                      <p className="mt-1 text-sm text-stone-500">
                        {location.code ? `Codice locale: ${location.code}` : 'Codice locale non impostato'}
                      </p>
                    </div>
                    {canUpdate && (
                      <Button
                        {...actionButtonProps(`settings-edit-location-${location.id}`, `Modifica ${location.name}`)}
                        onClick={() => onEdit(location)}
                      >
                        <Pencil className="mr-1 h-4 w-4" aria-hidden="true" /> Modifica
                      </Button>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <p className="rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-500">
                Nessun locale per questa azienda.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function SettingsDepartmentList({
  companies = [],
  locationsByCompany = {},
  departmentsByLocation = {},
  canCreate,
  canUpdate,
  canDelete,
  onCreate,
  onEdit,
  onDelete,
  testId = 'settings-departments',
}) {
  const hasLocations = companies.some((company) => (locationsByCompany[company.id] || []).length);
  if (!companies.length || !hasLocations) {
    return <SettingsEmptyState title="Nessun reparto disponibile" description="Non risultano reparti nel perimetro autorizzato." testId={`${testId}-empty`} />;
  }

  return (
    <div className="space-y-3" data-testid={testId}>
      {companies.flatMap((company) => (locationsByCompany[company.id] || []).map((location) => (
        <React.Fragment key={location.id}>
          {canCreate && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-2.5">
              <div className="min-w-0 text-sm">
                <span className="font-semibold text-stone-800">{location.name}</span>
                <span className="ml-2 text-stone-500">· {company.name}</span>
              </div>
              <Button
                {...actionButtonProps(`${testId}-add-${location.id}`, `Aggiungi un reparto a ${location.name}`)}
                onClick={() => onCreate(location.id)}
              >
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Aggiungi
              </Button>
            </div>
          )}
          {(departmentsByLocation[location.id] || []).map((department) => (
            <article
              key={department.id}
              className="flex min-w-0 items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5"
              data-testid={`settings-department-card-${department.id}`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                <Users className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate font-semibold text-stone-800">{department.name}</h2>
                <p className="mt-1 truncate text-sm text-stone-500">
                  Locale: {location.name} · Azienda: {company.name}
                </p>
                <p className="mt-1 text-xs text-stone-400">
                  {department.activeAssignments} assegnazioni attive
                  {department.totalAssignments !== department.activeAssignments && ` · ${department.totalAssignments} totali`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {canUpdate && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    data-testid={`${testId}-rename-${department.id}`}
                    onClick={() => onEdit(department)}
                    aria-label={`Rinomina ${department.name}`}
                    title="Rinomina"
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
                {canDelete && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`${testId}-delete-${department.id}`}
                    onClick={() => onDelete(department)}
                    aria-label={`Elimina ${department.name}`}
                    title="Elimina"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </article>
          ))}
        </React.Fragment>
      )))}
    </div>
  );
}